import React, { useState } from "react";
import styled from "styled-components";
import Popconfirm from "antd/es/popconfirm";
import Skeleton from "antd/es/skeleton";
import { Plus, Trash2 } from "lucide-react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import Drawer from "common/Drawers/Drawer.tsx";
import Select from "common/Select/Select.tsx";
import Input from "common/Inputs/Input.tsx";
import Button from "common/Buttons/Button.tsx";
import RequiredFieldMessage from "common/Animations/RequiredFieldMessage.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants.ts";
import { displayResultMessage, openMessage } from "utils/misc.tsx";
import {
  useCreateFinanceRuleMutation,
  useDeleteFinanceRuleMutation,
  useGetFinanceRulesQuery,
} from "app/services/finance.service";
import {
  FinanceCategory,
  FinanceRecategorizeResponse,
  FinanceRule,
  FinanceSource,
} from "features/finance/types";
import {
  ALL_SOURCES,
  CATEGORY_TO_LABEL,
  SOURCE_TO_LABEL,
} from "features/finance/constants";
import useCategoryOptions from "features/finance/useCategoryOptions";
import {
  Field,
  FieldHint,
  FieldLabel,
  Fields,
} from "features/finance/formStyles";

interface FinanceRulesDrawerProps {
  open: boolean;
  onClose: () => void;
}

const FinanceRulesDrawer = ({ open, onClose }: FinanceRulesDrawerProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const categoryOptions = useCategoryOptions();
  const [pattern, setPattern] = useState("");
  const [category, setCategory] = useState<FinanceCategory>();
  const [source, setSource] = useState<FinanceSource | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  const { data: rules, isLoading } = useGetFinanceRulesQuery(undefined, {
    skip: !open,
  });
  const [createRule, { isLoading: isCreating }] =
    useCreateFinanceRuleMutation();
  const [deleteRule] = useDeleteFinanceRuleMutation();
  const trimmedPattern = pattern.trim();

  const showRecategorized = (
    key: "finance.rules.created" | "finance.rules.deleted",
    response: FinanceRecategorizeResponse
  ) => openMessage(t(key, { count: response.recategorized_count }), "success");

  const onAdd = () => {
    if (!trimmedPattern || !category) {
      setShowErrors(true);
      return;
    }
    displayResultMessage(
      createRule({ pattern: trimmedPattern, category, source }).unwrap(),
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      false
    )
      .then((response) => {
        showRecategorized("finance.rules.created", response);
        setPattern("");
        setCategory(undefined);
        setSource(null);
        setShowErrors(false);
      })
      .catch(() => {});
  };

  const onDelete = (rule: FinanceRule) =>
    displayResultMessage(
      deleteRule({ rule_id: rule.id }).unwrap(),
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      false
    )
      .then((response) => showRecategorized("finance.rules.deleted", response))
      .catch(() => {});

  const sortedRules = [...(rules ?? [])].sort((a, b) =>
    a.pattern.localeCompare(b.pattern)
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement={isMobile ? "bottom" : "right"}
      size={isMobile ? "85%" : 520}
    >
      <Content>
        <DrawerTitle>{t("finance.rules.title")}</DrawerTitle>
        <FieldHint>{t("finance.rules.explanation")}</FieldHint>
        <AddForm>
          <Fields>
            <Field>
              <FieldLabel>{t("finance.rules.pattern")}</FieldLabel>
              <Input
                value={pattern}
                onChange={(event) => setPattern(event.target.value)}
                placeholder={t("finance.rules.pattern_placeholder")}
                onPressEnter={onAdd}
              />
              <RequiredFieldMessage
                requirement={showErrors && !trimmedPattern}
                errorMessage={t("finance.rules.errors.pattern")}
              />
            </Field>
            <FormRow>
              <Field>
                <FieldLabel>{t("finance.rules.category")}</FieldLabel>
                <Select
                  value={category}
                  options={categoryOptions}
                  onChange={setCategory}
                  placeholder={t("finance.rules.category")}
                  mobileTitle={t("finance.rules.category")}
                  style={{ width: "100%" }}
                />
                <RequiredFieldMessage
                  requirement={showErrors && !category}
                  errorMessage={t("finance.rules.errors.category")}
                />
              </Field>
              <Field>
                <FieldLabel>{t("finance.rules.source")}</FieldLabel>
                <Select
                  value={source ?? undefined}
                  allowClear={true}
                  options={ALL_SOURCES.map((value) => ({
                    value,
                    label: t(SOURCE_TO_LABEL[value]),
                  }))}
                  onChange={(value?: FinanceSource) => setSource(value ?? null)}
                  placeholder={t("finance.rules.any_source")}
                  mobileTitle={t("finance.rules.source")}
                  style={{ width: "100%" }}
                />
              </Field>
            </FormRow>
            <Button
              type="primary"
              onClick={onAdd}
              loading={isCreating}
              icon={<Plus size={16} strokeWidth={1.75} />}
            >
              {t("finance.rules.add")}
            </Button>
          </Fields>
        </AddForm>
        {isLoading ? (
          <Skeleton active={true} paragraph={{ rows: 6 }} />
        ) : !sortedRules.length ? (
          <Empty>{t("finance.rules.empty")}</Empty>
        ) : (
          <RuleList>
            {sortedRules.map((rule) => (
              <RuleRow key={rule.id}>
                <RuleText>
                  <Pattern title={rule.pattern}>{rule.pattern}</Pattern>
                  <RuleMeta>
                    {t(CATEGORY_TO_LABEL[rule.category])} ·{" "}
                    {rule.source
                      ? t(SOURCE_TO_LABEL[rule.source])
                      : t("finance.rules.any_source")}
                  </RuleMeta>
                </RuleText>
                <Popconfirm
                  title={t("finance.rules.delete_confirm")}
                  okText={t("finance.entries.delete")}
                  okButtonProps={{ danger: true }}
                  onConfirm={() => onDelete(rule)}
                >
                  <IconButton
                    type="text"
                    size="small"
                    aria-label={t("finance.entries.delete")}
                    icon={<Trash2 size={14} strokeWidth={1.75} />}
                  />
                </Popconfirm>
              </RuleRow>
            ))}
          </RuleList>
        )}
      </Content>
    </Drawer>
  );
};

export default FinanceRulesDrawer;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const DrawerTitle = styled.h2`
  margin: 0;
  padding-right: 32px;
  ${newLookType.pageTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const AddForm = styled.div`
  padding: 16px;
  border-radius: 10px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  border: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const RuleList = styled.div`
  display: flex;
  flex-direction: column;
`;
const RuleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 4px;
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  &:last-child {
    border-bottom: none;
  }
`;
const RuleText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;
const Pattern = styled.span`
  ${newLookType.body};
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const RuleMeta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Empty = styled.div`
  padding: 24px 0;
  text-align: center;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const IconButton = styled(Button)`
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
